'use client';
import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface LikeButtonProps {
  targetType: 'problem' | 'solution' | 'comment';
  targetId: string;
  initialCount?: number;
  size?: number;
  showCount?: boolean;
  className?: string;
}

export default function LikeButton({
  targetType,
  targetId,
  initialCount = 0,
  size = 13,
  showCount = true,
  className = '',
}: LikeButtonProps) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(initialCount);
  const [loading, setLoading] = useState(false);
  const [likeId, setLikeId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !targetId) return;
    async function checkLiked() {
      const supabase = createClient();
      const col = targetType === 'problem' ? 'problem_id' : targetType === 'solution' ? 'solution_id' : 'comment_id';
      const { data } = await supabase
        .from('likes')
        .select('id')
        .eq('user_id', user.id)
        .eq(col, targetId)
        .maybeSingle();
      if (data) {
        setLiked(true);
        setLikeId(data.id);
      }
    }
    checkLiked();
  }, [user, targetId, targetType]);

  async function handleToggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (!user) {
      window.location.href = '/sign-up-login-screen';
      return;
    }
    if (loading) return;
    setLoading(true);
    const supabase = createClient();
    const col = targetType === 'problem' ? 'problem_id' : targetType === 'solution' ? 'solution_id' : 'comment_id';

    if (liked && likeId) {
      const { error } = await supabase.from('likes').delete().eq('id', likeId);
      if (!error) {
        setLiked(false);
        setLikeId(null);
        setCount((c) => Math.max(c - 1, 0));
      }
    } else {
      const { data, error } = await supabase
        .from('likes')
        .insert({ user_id: user.id, [col]: targetId })
        .select('id')
        .single();
      if (!error && data) {
        setLiked(true);
        setLikeId(data.id);
        setCount((c) => c + 1);
      }
    }
    setLoading(false);
  }

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 ${
        liked
          ? 'bg-red-50 text-red-500 hover:bg-red-100' :'text-muted-foreground hover:bg-muted hover:text-foreground'
      } ${className}`}
      aria-label={liked ? 'Unlike' : 'Like'}
      suppressHydrationWarning
    >
      <Heart
        size={size}
        className={liked ? 'fill-red-500 text-red-500' : ''}
      />
      {showCount && <span>{count}</span>}
    </button>
  );
}
